import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/dramas";

interface HeroCarouselProps {
  items: Drama[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const timer = useRef<number | null>(null);

  const go = (index: number) => {
    setActive((index + items.length) % items.length);
  };

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  if (!items.length) return null;
  const current = items[active];

  return (
    <section className="relative w-full h-[460px] md:h-[560px] overflow-hidden bg-foreground-950">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={index !== active}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title}·出轨的妻子影视大全`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />

      <div className="relative z-10 h-full mx-auto max-w-7xl px-4 md:px-6 flex items-center">
        <div className="w-full max-w-2xl">
          <h1 className="text-xs md:text-sm tracking-[0.3em] text-secondary-300 font-label">
            出轨的妻子 · 影视大全
          </h1>
          <h2 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-white leading-tight">
            {current.title}
          </h2>
          <div className="mt-4 flex flex-wrap items-center gap-2.5 text-xs md:text-sm text-white/85">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 font-medium text-white">{current.type}</span>
            <span>{current.year}</span>
            <span className="w-px h-3.5 bg-white/30" />
            <span>{current.genre}</span>
            {current.episodes > 0 && (
              <>
                <span className="w-px h-3.5 bg-white/30" />
                <span>全 {current.episodes} 集</span>
              </>
            )}
            <span className="w-px h-3.5 bg-white/30" />
            <span className="flex items-center gap-1 font-semibold text-secondary-300">
              <i className="ri-star-fill" />
              {current.rating.toFixed(1)}
            </span>
          </div>
          <p className="mt-5 text-sm md:text-base leading-7 text-white/80 line-clamp-3 max-w-xl">
            {current.summary}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg" />
              查看剧情与影评
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/40 px-5 py-2.5 text-sm font-medium text-white hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-grid-line" />
              浏览全部影视
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3">
        <button
          type="button"
          aria-label="上一部"
          onClick={() => go(active - 1)}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/30 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <div className="flex items-center gap-2">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => go(index)}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                index === active ? "w-7 bg-secondary-400" : "w-1.5 bg-white/50"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label="下一部"
          onClick={() => go(active + 1)}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm hover:bg-white/30 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>
    </section>
  );
}